import { translate } from "app/language/service"
import { IRootState } from "app/model"
import { CampaignSelect } from "campaign/components/misc/CampaignSelect"
import { ICampaign } from "campaign/model"
import { getCampaignsById } from "campaign/tools/common"
import { EnvironmentSelect } from "environments/components/EnvironmentSelect"
import { IEnvironment } from "environments/model"
import { getEnvironmentsByIds } from "environments/tools/common"
import { MediaSelect } from "media/components/MediaSelect"
import { IMediaDTO } from "media/model"
import { getMediasById } from "media/tools/common"
import { OperatorSelect } from "operator/components/OperatorSelect"
import { IOperator } from "operator/model"
import { getOperatorsById } from "operator/tools/common"
import { OrderSelect } from "order/components/OrderSelect"
import { IOrder } from "order/model"
import { getOrderByIds } from "order/tools/common"
import { UnexpectedErrorPage } from "page/components/UnexpectedErrorPage"
import React from "react"
import { connect } from "react-redux"
import { IReportFilters } from "reports/model"
import { setReportsParameters } from "reports/tools/common"
import { getReportForm } from "reports/tools/ui"
import { WeatherSelect } from "schedule/components/WeatherSelect"
import { CitySelect } from "shape/components/CitySelect"
import { CountrySelect } from "shape/components/CountrySelect"
import { PostalCodeSelect } from "shape/components/PostalCodeSelect"
import { StateSelect } from "shape/components/StateSelect"
import { IGeoShapeCountry, IGeoShapePostalCode, IGeoShapeState, ILookupCity } from "shape/model"
import {
  getCitiesByNames,
  getCountriesById,
  getPostalCodesWithOnesEndingByAStar,
  getStatesByIds
} from "shape/tools/common"
import { SSPSelect } from "ssp/components/SSPSelect"
import { ISsp } from "ssp/model"
import { getSspByIds } from "ssp/tools/common"
import { FlexBox } from "ui/components/FlexBox"
import { LinearLoader } from "ui/components/LinearLoader"
import { YesNotSelect } from "ui/components/YesNotSelect"
import { addImportant } from "ui/tools/common"
import { StartEndDateFilter } from "./StartEndDateFilter"

interface IReportParametersFiltersProps {
  isCreation: boolean
}

interface IReportParametersFiltersConnectProps {
  filters: IReportFilters
}

interface IDefaultData {
  campaigns?: ICampaign[]
  orders?: IOrder[]
  ssps?: ISsp[]
  publishers?: IOperator[]
  medias?: IMediaDTO[]
  environments?: IEnvironment[]
  cities?: ILookupCity[]
  postalCodes?: IGeoShapePostalCode[]
  states?: IGeoShapeState[]
  countries?: IGeoShapeCountry[]
}

const ReportParametersFiltersFn = React.memo(
  ({ isCreation, filters }: IReportParametersFiltersProps & IReportParametersFiltersConnectProps) => {
    const [isLoading, setIsLoading] = React.useState(!isCreation)
    const [hasLoadingError, setHasLoadingError] = React.useState(false)
    const [defaultData, setDefaultData] = React.useState<IDefaultData>({})
    React.useEffect(() => {
      if (isCreation) {
        return
      }
      const newDefaultData: IDefaultData = {}
      const allPromises = []
      if (filters.campaignIds && filters.campaignIds.length > 0) {
        allPromises.push(
          (async () => (newDefaultData.campaigns = await getCampaignsById(filters.campaignIds as number[])))()
        )
      }
      if (filters.orderIds && filters.orderIds.length > 0) {
        allPromises.push((async () => (newDefaultData.orders = await getOrderByIds(filters.orderIds as number[])))())
      }
      if (filters.sspIds && filters.sspIds.length > 0) {
        allPromises.push((async () => (newDefaultData.ssps = await getSspByIds(filters.sspIds as number[])))())
      }
      if (filters.publisherIds && filters.publisherIds.length > 0) {
        allPromises.push(
          (async () => (newDefaultData.publishers = await getOperatorsById(filters.publisherIds as string[])))()
        )
      }
      if (filters.venueTypeIds && filters.venueTypeIds.length > 0) {
        allPromises.push(
          (async () => (newDefaultData.environments = await getEnvironmentsByIds(filters.venueTypeIds as number[])))()
        )
      }
      if (filters.cityIds && filters.cityIds.length > 0) {
        allPromises.push((async () => (newDefaultData.cities = await getCitiesByNames(filters.cityIds as string[])))())
      }
      if (filters.postalCodes && filters.postalCodes.length > 0) {
        allPromises.push(
          (async () =>
            (newDefaultData.postalCodes = await getPostalCodesWithOnesEndingByAStar(filters.postalCodes as string[])))()
        )
      }
      if (filters.stateIds && filters.stateIds.length > 0) {
        allPromises.push((async () => (newDefaultData.states = await getStatesByIds(filters.stateIds as string[])))())
      }
      if (filters.countryIds && filters.countryIds.length > 0) {
        allPromises.push(
          (async () => (newDefaultData.countries = await getCountriesById(filters.countryIds as string[])))()
        )
      }
      if (filters.mediaIds && filters.mediaIds.length > 0) {
        allPromises.push((async () => (newDefaultData.medias = await getMediasById(filters.mediaIds as number[])))())
      }
      if (allPromises.length === 0) {
        setIsLoading(false)
        return
      }
      Promise.all(allPromises)
        .then(() => {
          setDefaultData(newDefaultData)
          setIsLoading(false)
        })
        .catch(error => {
          console.error(error)
          setHasLoadingError(true)
          setIsLoading(false)
        })
    }, [])
    if (isLoading) {
      return <LinearLoader text={translate("label.loading.data")} />
    }
    if (hasLoadingError) {
      return <UnexpectedErrorPage />
    }
    return (
      <FlexBox
        id="reports-filters-div-container"
        jc="space-around"
        wrap
        fullWidth
        gap
        sx={{ "& > *": { width: addImportant("18rem") } }}>
        <CampaignSelect
          defaultCampaigns={defaultData.campaigns}
          onChange={campaigns =>
            setReportsParameters(isCreation, { filters: { ...filters, campaignIds: campaigns.map(c => c.id) } })
          }
        />
        <OrderSelect
          defaultOrders={defaultData.orders}
          onChange={orders =>
            setReportsParameters(isCreation, { filters: { ...filters, orderIds: orders.map(o => o.id) } })
          }
        />
        <SSPSelect
          defaultSsps={defaultData.ssps}
          onChange={ssps => setReportsParameters(isCreation, { filters: { ...filters, sspIds: ssps.map(s => s.id) } })}
        />
        <OperatorSelect
          defaultOperators={defaultData.publishers}
          onChange={operators =>
            setReportsParameters(isCreation, {
              filters: { ...filters, publisherIds: operators.map(o => o.publisher_name) }
            })
          }
        />
        <EnvironmentSelect
          defaultEnvironments={defaultData.environments}
          onChange={environments =>
            setReportsParameters(isCreation, { filters: { ...filters, venueTypeIds: environments.map(e => e.id) } })
          }
        />
        <WeatherSelect
          defaultWeatherIds={filters.weatherIds}
          onChange={weatherIds => setReportsParameters(isCreation, { filters: { ...filters, weatherIds } })}
        />
        <CitySelect
          defaultCities={defaultData.cities}
          onChange={cities =>
            setReportsParameters(isCreation, { filters: { ...filters, cityIds: cities.map(c => c.name) } })
          }
        />
        <PostalCodeSelect
          defaultPostalCodes={defaultData.postalCodes}
          onChange={postalCodes =>
            setReportsParameters(isCreation, {
              filters: { ...filters, postalCodes: postalCodes.map(p => p.postal_code) }
            })
          }
        />
        <StateSelect
          defaultStates={defaultData.states}
          onChange={states =>
            setReportsParameters(isCreation, { filters: { ...filters, stateIds: states.map(s => s.state_shape_id) } })
          }
        />
        <CountrySelect
          defaultCountries={defaultData.countries}
          onChange={countries =>
            setReportsParameters(isCreation, {
              filters: { ...filters, countryIds: countries.map(c => c.country_shape_id) }
            })
          }
        />
        <MediaSelect
          defaultMedias={defaultData.medias}
          onChange={medias =>
            setReportsParameters(isCreation, { filters: { ...filters, mediaIds: medias.map(m => m.id) } })
          }
        />
        <YesNotSelect
          id="reports-media_is_portrait"
          mainLabel={translate("report.group.sort.by.media_is_portrait")}
          value={filters.mediaIsPortrait}
          onChange={isChecked =>
            setReportsParameters(isCreation, { filters: { ...filters, mediaIsPortrait: isChecked } })
          }
        />
        <YesNotSelect
          id="reports-media_is_video"
          mainLabel={translate("report.group.sort.by.media_is_video")}
          value={filters.mediaIsVideo}
          onChange={isChecked => setReportsParameters(isCreation, { filters: { ...filters, mediaIsVideo: isChecked } })}
        />
        <StartEndDateFilter filters={filters} isCreation={isCreation} />
      </FlexBox>
    )
  }
)

export const ReportParametersFilters = connect(
  (state: IRootState, ownProps: IReportParametersFiltersProps): IReportParametersFiltersConnectProps => ({
    filters: getReportForm(ownProps.isCreation, state.reports).parameters.filters
  })
)(ReportParametersFiltersFn)
